import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Mail, Download, Loader2, FileText, CheckCircle2, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GUIDE_PREVIEWS = [
  { title: 'Claude Code: 25 Tips & Tricks', subtitle: 'Pro patterns from real coding sessions' },
  { title: 'ChatGPT Prompting Playbook', subtitle: '30 proven patterns that get 10x better answers' },
  { title: 'Hot AI Models This Month', subtitle: 'New models worth trying — explained simply' },
];

const GuidesPdf = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleUnlock = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      // Read ?ref= from the current URL so referral credits work
      let refCode = '';
      try {
        const params = new URLSearchParams(window.location.search);
        refCode = params.get('ref') || localStorage.getItem('referral_code') || '';
        if (params.get('ref')) localStorage.setItem('referral_code', params.get('ref'));
      } catch { /* ignore */ }

      const res = await axios.post(
        `${API_URL}/api/guides/unlock-pdf`,
        { email: value, source: 'guides_pdf_landing', ref_code: refCode || undefined },
        { responseType: 'blob', timeout: 120000 },
      );
      // Trigger browser download
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ai-banter-free-guides.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      try { localStorage.setItem('subscriber_email', value); } catch {}
      window.dispatchEvent(new CustomEvent('subscriber-changed', { detail: { email: value } }));
      setDownloaded(true);
      toast.success('Downloaded — check your inbox for your welcome email too.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not unlock — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="guides-pdf-page">
      <SEO
        title="Free AI Guides PDF — Claude Code, ChatGPT Prompting, Hot AI Models"
        description="Drop your email to instantly download the free AI guide bundle: 25 Claude Code tips, 30 ChatGPT prompting patterns, and the hottest AI models worth trying — all in one polished PDF."
        path="/guides-pdf"
        keywords={['free AI guide PDF','Claude Code tips PDF','ChatGPT prompting PDF','Hot AI models guide','AI ethics for teens']}
      />

      <section className="pt-32 pb-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-semibold tracking-wider uppercase mb-4">
            Free PDF · One-click download
          </span>
          <h1 className="text-5xl md:text-6xl font-light mb-5 tracking-tight leading-tight">
            Three free AI guides.<br/>
            One PDF. Yours instantly.
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
            Drop your email, get the bundle as a polished PDF you can read offline,
            and start receiving free weekly bite-sized AI ethics lessons. No spam, unsubscribe anytime.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Preview pane */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3">Inside the PDF</h3>
            {GUIDE_PREVIEWS.map((g, i) => (
              <div key={i} className="p-5 rounded-xl border border-gray-200 bg-white" data-testid={`preview-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-black text-white flex items-center justify-center flex-shrink-0">
                    <FileText size={16} />
                  </div>
                  <div>
                    <div className="text-base font-medium text-gray-900 leading-snug">{g.title}</div>
                    <div className="text-sm text-gray-600 mt-1">{g.subtitle}</div>
                  </div>
                </div>
              </div>
            ))}
            <p className="text-xs text-gray-500 pt-2">
              Bonus: every Sunday subscribers get a fresh bite-sized AI lesson digest.
            </p>
          </div>

          {/* Unlock form */}
          <div className="lg:col-span-3">
            {!downloaded ? (
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-black text-white mb-5">
                  <Lock size={20} />
                </div>
                <h2 className="text-2xl font-medium mb-2">Unlock the PDF</h2>
                <p className="text-gray-600 mb-6">
                  Enter your email below — the PDF starts downloading instantly and the welcome email lands in your inbox in under a minute.
                </p>
                <form onSubmit={handleUnlock} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                      data-testid="guides-pdf-email-input"
                      autoComplete="email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 inline-flex items-center justify-center gap-2 font-medium"
                    data-testid="guides-pdf-unlock-btn"
                  >
                    {submitting ? (
                      <><Loader2 size={18} className="animate-spin" /> Generating your PDF…</>
                    ) : (
                      <><Download size={18} /> Download free PDF</>
                    )}
                  </button>
                </form>
                <p className="text-xs text-gray-400 mt-4">
                  By subscribing you agree to receive emails from Riddhi. One click to unsubscribe. Your email is never shared.
                </p>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center" data-testid="guides-pdf-success">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500 text-white mb-5">
                  <CheckCircle2 size={26} />
                </div>
                <h2 className="text-2xl font-medium mb-2">PDF on its way 🎉</h2>
                <p className="text-gray-700 mb-6">
                  Your download started. If it didn't, click the button below to retry. We've also dropped a welcome email in your inbox.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => { setDownloaded(false); setEmail(''); }}
                    className="px-5 py-2.5 border border-gray-300 rounded-md hover:bg-white"
                    data-testid="guides-pdf-retry"
                  >
                    Use a different email
                  </button>
                  <Link
                    to="/refer"
                    className="px-5 py-2.5 bg-black text-white rounded-md hover:bg-gray-800 inline-flex items-center justify-center gap-1"
                  >
                    Refer friends, unlock more <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuidesPdf;
